import { useState, useEffect, useRef, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { DatePicker } from '@/components/ui/date-picker';
import { toDateParam, parseDateOnly } from '@/lib/date';
import { PhoneInput } from '@/components/ui/phone-input';
import { AddressForm } from '@/components/ui/address-form';
import { usePositions, useDepartments, positionsKeys } from '@/hooks/use-organizations';
import { updateEmployee } from '@/lib/employees';
import { employeesKeys } from '@/hooks/use-employees';
import type { EmployeeDetail } from '@/types/employee';
import { Loader2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface EditEmployeeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employee: EmployeeDetail;
}

export function EditEmployeeDialog({ open, onOpenChange, employee }: EditEmployeeDialogProps) {
  const queryClient = useQueryClient();
  const { data: positions = [], isLoading: positionsLoading } = usePositions();
  const { data: departments = [], isLoading: departmentsLoading } = useDepartments();

  // Uncontrolled text inputs via refs — avoids re-renders on every keystroke
  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const preferredNameRef = useRef<HTMLInputElement>(null);
  const workEmailRef = useRef<HTMLInputElement>(null);
  const personalEmailRef = useRef<HTMLInputElement>(null);
  const corporateCvUrlRef = useRef<HTMLInputElement>(null);

  // Controlled state only for components that require it (Select, DatePicker, Phone, Address)
  const [positionId, setPositionId] = useState<string>(employee.positionId || '');
  const [departmentId, setDepartmentId] = useState<string>(employee.departmentId || '');
  const [dateOfBirth, setDateOfBirth] = useState<Date | null>(
    employee.personalInfo?.dateOfBirth
      ? parseDateOnly(employee.personalInfo.dateOfBirth)
      : null
  );
  const [phone, setPhone] = useState<string>(employee.personalInfo?.phone || '');
  const addressRef = useRef<string>(employee.personalInfo?.address || '');
  const [formKey, setFormKey] = useState(0);

  const [errors, setErrors] = useState<{
    workEmail?: string;
    personalEmail?: string;
    corporateCvUrl?: string;
  }>({});

  useEffect(() => {
    if (open) {
      setPositionId(employee.positionId || '');
      setDepartmentId(employee.departmentId || '');
      setDateOfBirth(
        employee.personalInfo?.dateOfBirth
          ? parseDateOnly(employee.personalInfo.dateOfBirth)
          : null
      );
      setPhone(employee.personalInfo?.phone || '');
      addressRef.current = employee.personalInfo?.address || '';
      setErrors({});
      setFormKey((k) => k + 1);
    }
  }, [
    open,
    employee.positionId,
    employee.departmentId,
    employee.personalInfo?.dateOfBirth,
    employee.personalInfo?.phone,
    employee.personalInfo?.address,
  ]);

  const validateEmail = (email: string): boolean => {
    if (!email.trim()) return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateHttpUrl = (url: string): boolean => {
    if (!url.trim()) return true;
    try {
      const parsed = new URL(url.trim());
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handlePhoneChange = useCallback((value: string | undefined) => {
    setPhone(value || '');
  }, []);

  const handleAddressChange = useCallback((value: string) => {
    addressRef.current = value;
  }, []);

  const handleWorkEmailBlur = () => {
    const val = workEmailRef.current?.value || '';
    if (val && !validateEmail(val)) {
      setErrors((prev) => ({ ...prev, workEmail: 'Please enter a valid email address' }));
    } else {
      setErrors((prev) => {
        if (!prev.workEmail) return prev;
        const { workEmail: _workEmail, ...rest } = prev;
        return rest;
      });
    }
  };

  const handlePersonalEmailBlur = () => {
    const val = personalEmailRef.current?.value || '';
    if (val && !validateEmail(val)) {
      setErrors((prev) => ({ ...prev, personalEmail: 'Please enter a valid email address' }));
    } else {
      setErrors((prev) => {
        if (!prev.personalEmail) return prev;
        const { personalEmail: _personalEmail, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleCorporateCvUrlBlur = () => {
    const val = corporateCvUrlRef.current?.value || '';
    if (val && !validateHttpUrl(val)) {
      setErrors((prev) => ({
        ...prev,
        corporateCvUrl: 'Please enter a valid http or https URL',
      }));
    } else {
      setErrors((prev) => {
        if (!prev.corporateCvUrl) return prev;
        const { corporateCvUrl: _corporateCvUrl, ...rest } = prev;
        return rest;
      });
    }
  };

  const updateMutation = useMutation({
    mutationFn: (input: Parameters<typeof updateEmployee>[1]) =>
      updateEmployee(employee.id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: employeesKeys.detail(employee.id) });
      queryClient.invalidateQueries({ queryKey: employeesKeys.lists() });
      // A profile edit can reassign the position → refresh the Settings → Positions
      // employee counts (positionsKeys), else that list is stale until a reload.
      queryClient.invalidateQueries({ queryKey: positionsKeys.all });
      onOpenChange(false);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const firstName = firstNameRef.current?.value.trim() ?? '';
    const lastName = lastNameRef.current?.value.trim() ?? '';
    const preferredName = preferredNameRef.current?.value.trim() ?? '';
    const workEmail = workEmailRef.current?.value.trim() ?? '';
    const personalEmail = personalEmailRef.current?.value.trim() ?? '';
    const corporateCvUrl = corporateCvUrlRef.current?.value.trim() ?? '';

    const workEmailValid = validateEmail(workEmail);
    const personalEmailValid = !personalEmail || validateEmail(personalEmail);
    const corporateCvUrlValid = !corporateCvUrl || validateHttpUrl(corporateCvUrl);

    if (!workEmailValid || !personalEmailValid || !corporateCvUrlValid) {
      setErrors({
        workEmail: !workEmailValid ? 'Please enter a valid email address' : undefined,
        personalEmail: !personalEmailValid ? 'Please enter a valid email address' : undefined,
        corporateCvUrl: !corporateCvUrlValid
          ? 'Please enter a valid http or https URL'
          : undefined,
      });
      return;
    }

    updateMutation.mutate({
      firstName,
      lastName,
      preferredName: preferredName || null,
      workEmail,
      positionId: positionId || null,
      departmentId: departmentId || null,
      personalInfo: {
        dateOfBirth: toDateParam(dateOfBirth),
        phone: phone ? phone.trim() || null : null,
        personalEmail: personalEmail || null,
        address: addressRef.current.trim() || null,
        corporateCvUrl: corporateCvUrl || null,
      },
    });
  };

  const isLoading = positionsLoading || departmentsLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Employee</DialogTitle>
          <DialogDescription>
            Update the employee's basic information and job details.
          </DialogDescription>
        </DialogHeader>
        <form key={formKey} onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  ref={firstNameRef}
                  id="firstName"
                  defaultValue={employee.firstName || ''}
                  disabled={updateMutation.isPending}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  ref={lastNameRef}
                  id="lastName"
                  defaultValue={employee.lastName || ''}
                  disabled={updateMutation.isPending}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="preferredName">Preferred Name</Label>
              <Input
                ref={preferredNameRef}
                id="preferredName"
                defaultValue={employee.preferredName || ''}
                disabled={updateMutation.isPending}
                placeholder="Optional"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="workEmail">Work Email *</Label>
              <div className="space-y-1">
                <Input
                  ref={workEmailRef}
                  id="workEmail"
                  type="email"
                  defaultValue={employee.workEmail || ''}
                  onBlur={handleWorkEmailBlur}
                  disabled={updateMutation.isPending}
                  required
                  className={cn(errors.workEmail && 'border-red-500 focus-visible:ring-red-500')}
                />
                {errors.workEmail && (
                  <div className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400">
                    <AlertCircle className="h-3.5 w-3.5" />
                    {errors.workEmail}
                  </div>
                )}
              </div>
            </div>

            <div className="border-t pt-4 space-y-4">
              <h3 className="text-sm font-semibold text-foreground">Personal Information</h3>

              <div className="space-y-2">
                <Label htmlFor="dateOfBirth">Date of Birth</Label>
                <DatePicker
                  id="dateOfBirth"
                  value={dateOfBirth}
                  onChange={setDateOfBirth}
                  disabled={updateMutation.isPending}
                  placeholder="Select date of birth"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <PhoneInput
                  id="phone"
                  value={phone}
                  onChange={handlePhoneChange}
                  disabled={updateMutation.isPending}
                  placeholder="Enter phone number"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="personalEmail">Personal Email</Label>
                <div className="space-y-1">
                  <Input
                    ref={personalEmailRef}
                    id="personalEmail"
                    type="email"
                    defaultValue={employee.personalInfo?.personalEmail || ''}
                    onBlur={handlePersonalEmailBlur}
                    disabled={updateMutation.isPending}
                    placeholder="personal@example.com"
                    className={cn(errors.personalEmail && 'border-red-500 focus-visible:ring-red-500')}
                  />
                  {errors.personalEmail && (
                    <div className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400">
                      <AlertCircle className="h-3.5 w-3.5" />
                      {errors.personalEmail}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="corporateCvUrl">Corporate CV</Label>
                <div className="space-y-1">
                  <Input
                    ref={corporateCvUrlRef}
                    id="corporateCvUrl"
                    type="url"
                    inputMode="url"
                    defaultValue={employee.personalInfo?.corporateCvUrl || ''}
                    onBlur={handleCorporateCvUrlBlur}
                    disabled={updateMutation.isPending}
                    placeholder="https://example.com/cv.pdf"
                    className={cn(errors.corporateCvUrl && 'border-red-500 focus-visible:ring-red-500')}
                  />
                  {errors.corporateCvUrl && (
                    <div className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400">
                      <AlertCircle className="h-3.5 w-3.5" />
                      {errors.corporateCvUrl}
                    </div>
                  )}
                </div>
              </div>

            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Position</Label>
                <SearchableSelect
                  value={positionId}
                  onChange={setPositionId}
                  options={positions.map((p) => ({ id: p.id, name: p.title }))}
                  placeholder="Select position"
                  emptyText="No results found"
                  disabled={isLoading || updateMutation.isPending}
                  modal
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="department">Department</Label>
                <Select
                  value={departmentId || '__none__'}
                  onValueChange={(value) => setDepartmentId(value === '__none__' ? '' : value)}
                  disabled={isLoading || updateMutation.isPending}
                >
                  <SelectTrigger id="department">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {departments.map((department) => (
                      <SelectItem key={department.id} value={department.id}>
                        {department.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Location & Address</Label>
              <AddressForm
                value={addressRef.current}
                onValueChange={handleAddressChange}
                disabled={updateMutation.isPending}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
